import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import type {OfferingCreateDraftScope} from "./build-storage-key";
import {deserializeDraft} from "./deserialize-draft";
import {serializeDraft} from "./serialize-draft";
import {
  clearOfferingCreateDraft,
  readOfferingCreateDraft,
  writeOfferingCreateDraft,
} from "./clear-offering-create-draft";

export {clearOfferingCreateDraft};

const DEFAULT_CLEAR_ACTIONS = ["reset-state"];
const PERSIST_DEBOUNCE_MS = 300;

type ActionWithType = {type: string};

function getActionType(action: unknown): string | undefined {
  if (action && typeof action === "object" && "type" in action) {
    return String((action as ActionWithType).type);
  }
  return undefined;
}

export type PersistedReducerMeta = {
  wasDraftRestored: boolean;
};

export function usePersistedReducer<S, A extends ActionWithType>(
  reducer: (state: S, action: A) => S,
  scope: OfferingCreateDraftScope,
  getInitialState: () => S,
  options?: {clearOnActions?: string[]}
): [S, Dispatch<A>, PersistedReducerMeta] {
  const clearOnActions = options?.clearOnActions ?? DEFAULT_CLEAR_ACTIONS;
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextPersistRef = useRef(false);
  const isInitialMountRef = useRef(true);

  const [init] = useState(() => {
    const fallback = getInitialState();
    const raw = readOfferingCreateDraft(scope);
    const state = deserializeDraft(raw, fallback);
    return {state, wasDraftRestored: state !== fallback};
  });

  const [state, baseDispatch] = useReducer(reducer, init.state);

  const persistState = useCallback(
    (nextState: S) => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
      persistTimeoutRef.current = setTimeout(() => {
        writeOfferingCreateDraft(scope, serializeDraft(nextState));
      }, PERSIST_DEBOUNCE_MS);
    },
    [scope]
  );

  const dispatch = useCallback<Dispatch<A>>(
    (action) => {
      const actionType = getActionType(action);
      if (actionType && clearOnActions.includes(actionType)) {
        if (persistTimeoutRef.current) {
          clearTimeout(persistTimeoutRef.current);
          persistTimeoutRef.current = null;
        }
        clearOfferingCreateDraft(scope);
        skipNextPersistRef.current = true;
        baseDispatch(action);
        return;
      }

      baseDispatch(action);
    },
    [baseDispatch, clearOnActions, scope]
  );

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    persistState(state);
  }, [persistState, state]);

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
    };
  }, []);

  const meta = useMemo(
    (): PersistedReducerMeta => ({wasDraftRestored: init.wasDraftRestored}),
    [init.wasDraftRestored]
  );

  return useMemo(
    (): [S, Dispatch<A>, PersistedReducerMeta] => [state, dispatch, meta],
    [dispatch, meta, state]
  );
}
